import isEqual from "lodash/isEqual";
import { v4 as uuidv4 } from "uuid";
import {
    appendQaStatusToLeafBandPayload,
    getExecutiveSummaryBandQaStatus,
    getExecutiveSummaryBuildingQaStatus,
    isExecutiveSummaryLeafBand
} from "./executiveSummaryQaStatus";

/**
 * Executive summary bands — canonical image band shape:
 * { type: "image_band", label, key, id?, data: [{ id }], config: { type: 1|2|3|4 } }
 * Legacy: band_kind "image", images: [{ image_id }], image_layout
 */

export function generateExecSummaryImageBandId() {
    return uuidv4();
}

/** UUID for any executive summary band type (image, additional, comb_band, etc.). */
export function generateExecSummaryBandId() {
    return uuidv4();
}

export function isExecutiveSummaryCombBand(band) {
    return band != null && typeof band === "object" && band.type === "comb_band";
}

export function isExecutiveSummaryTradeBand(band) {
    return band != null && typeof band === "object" && band.type === "trade";
}

/** Leaf text bands that support template sample_text (additional + trade only). */
export function isExecutiveSummarySampleTextBand(band) {
    if (!band || typeof band !== "object") return false;
    if (isExecutiveSummaryImageBand(band) || isExecutiveSummaryCombBand(band)) return false;
    const type = band.type ?? "additional";
    return type === "additional" || type === "trade";
}

export function getExecutiveSummaryBandSampleText(band) {
    if (!band || typeof band !== "object") return "";
    return band.sample_text ?? "";
}

export function hasExecutiveSummaryBandSampleText(band) {
    return (
        isExecutiveSummarySampleTextBand(band) &&
        stripExecutiveSummaryHtml(getExecutiveSummaryBandSampleText(band)).length > 0
    );
}

function appendSampleTextToLeafBandPayload(payload, band) {
    if (!isExecutiveSummarySampleTextBand(band)) return payload;
    return { ...payload, sample_text: getExecutiveSummaryBandSampleText(band) };
}

export function getExecutiveSummaryBandLabel(band) {
    if (!band || typeof band !== "object") return "";
    return String(band.label ?? band.name ?? "").trim();
}

export function getCombBandChildren(band) {
    if (!isExecutiveSummaryCombBand(band)) return [];
    return Array.isArray(band.bands) ? band.bands : [];
}

export function toExecSummaryImageDataEntry(imageId) {
    if (imageId == null || String(imageId) === "") return null;
    return { id: String(imageId) };
}

export function slimExecSummaryImageDataEntries(entries) {
    return (Array.isArray(entries) ? entries : [])
        .map(e => toExecSummaryImageDataEntry(e?.id ?? e?.image_id))
        .filter(Boolean);
}

export const EXEC_SUMMARY_IMAGE_LAYOUT = {
    SINGLE_STANDARD: 1,
    SINGLE_WIDE: 2,
    DOUBLE: 3,
    TRIPLE: 4
};

export const EXEC_SUMMARY_IMAGE_BAND_MAX_IMAGES = 50;

export const EXEC_SUMMARY_LAYOUT_OPTIONS = [
    { value: 1, label: "Standard (1 per row)" },
    { value: 2, label: "Featured (1 wide per row)" },
    { value: 3, label: "Split (2 per row)" },
    { value: 4, label: "Row (3 per row)" }
];

const IMAGE_DATA_KEYS = [
    "source",
    "name",
    "is_edited",
    "s3_thumbnail_key",
    "s3_ethumbnail_key",
    "s3_image_key",
    "s3_eimage_key",
    "s3_image_url",
    "s3_thumbnail_url",
    "thumbnail_url",
    "medium_image_url",
    "image_url",
    "caption",
    "url"
];

export function parseExecutiveSummaryApiResponse(res) {
    if (!res || res.success === false) return [];
    const list = res.data?.executive_summary ?? res.executive_summary;
    return Array.isArray(list) ? list : [];
}

/** Per-user autosave draft returned alongside the saved summary. Null when absent. */
export function parseExecutiveSummaryDraftFromApiResponse(res) {
    if (!res || res.success === false) return null;
    const draft = res.data?.draft ?? res.draft;
    if (!draft || !Array.isArray(draft.executive_summary)) return null;
    return {
        bands: draft.executive_summary,
        updatedAt: draft.updated_at ?? null,
        summaryChanged: !!draft.summary_changed_since_draft
    };
}

export function isExecutiveSummaryImageBand(band) {
    if (!band || typeof band !== "object") return false;
    if (band.type === "image_band") return true;
    const bk = String(band.band_kind ?? "").toLowerCase();
    if (bk === "image" || bk === "images" || bk === "image_band") return true;
    const t = band.type;
    if (t === "image" || t === "images") return true;
    if (Array.isArray(band.data) && band.config != null && typeof band.config === "object") return true;
    return false;
}

export function getExecSummaryImageLayout(band) {
    if (!band || typeof band !== "object") return EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_STANDARD;
    const n = Number(band.config?.type ?? band.image_layout);
    if (Number.isFinite(n) && n >= 1 && n <= 4) return n;
    return EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_STANDARD;
}

/** Columns per row for CSS grid (not max image count). */
export function getExecutiveSummaryImagesPerRow(layout) {
    const n = Number(layout);
    switch (n) {
        case EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_STANDARD:
        case EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_WIDE:
            return 1;
        case EXEC_SUMMARY_IMAGE_LAYOUT.DOUBLE:
            return 2;
        case EXEC_SUMMARY_IMAGE_LAYOUT.TRIPLE:
            return 3;
        default:
            return 1;
    }
}

/** @deprecated Use getExecutiveSummaryImagesPerRow — kept for transitional imports. */
export function getExecutiveSummaryImageSlotCount(layout) {
    return getExecutiveSummaryImagesPerRow(layout);
}

export function getExecutiveSummaryLayoutLabel(layout) {
    const n = Number(layout);
    const opt = EXEC_SUMMARY_LAYOUT_OPTIONS.find(o => o.value === n);
    return opt ? opt.label : EXEC_SUMMARY_LAYOUT_OPTIONS[0].label;
}

export function clampExecutiveSummaryImageData(data) {
    const list = Array.isArray(data) ? data : [];
    return list.slice(0, EXEC_SUMMARY_IMAGE_BAND_MAX_IMAGES);
}

export function formatExecutiveImageBandSubheading(band) {
    if (!band || !isExecutiveSummaryImageBand(band)) return "";
    const count = getExecSummaryImageData(band).length;
    const layoutLabel = getExecutiveSummaryLayoutLabel(getExecSummaryImageLayout(band));
    const countLabel = count === 0 ? "No images" : count === 1 ? "1 image" : `${count} images`;
    return `${countLabel} · ${layoutLabel}`;
}

export function formatExecutiveImageBandLayoutOnly(band) {
    if (!band || !isExecutiveSummaryImageBand(band)) return "";
    return getExecutiveSummaryLayoutLabel(getExecSummaryImageLayout(band));
}

/** Apply a new layout type; preserves all assigned images (grid reflow only). */
export function applyExecutiveImageBandLayoutChange(band, newLayoutType) {
    if (!band || typeof band !== "object" || !isExecutiveSummaryImageBand(band)) return band;
    const n = Number(newLayoutType);
    if (!Number.isFinite(n) || n < 1 || n > 4) return band;
    const data = normalizeExecSummaryImageDataForEditor(getExecSummaryImageData(band));
    return {
        ...band,
        type: "image_band",
        data,
        config: { ...(band.config && typeof band.config === "object" ? band.config : {}), type: n },
        band_kind: undefined,
        image_layout: undefined,
        images: undefined
    };
}

function copyPersistedImageFields(entry) {
    if (!entry || typeof entry !== "object") return {};
    const out = {};
    IMAGE_DATA_KEYS.forEach(k => {
        const v = entry[k];
        if (k === "is_edited" && typeof entry.is_edited === "boolean") {
            out.is_edited = entry.is_edited;
            return;
        }
        if (v != null && v !== "") out[k] = v;
    });
    return out;
}

export function getExecSummaryImageData(band) {
    if (!band || typeof band !== "object") return [];
    if (Array.isArray(band.data) && band.data.length) {
        return band.data
            .map(entry => {
                if (!entry || typeof entry !== "object") return null;
                const id = entry.id ?? entry.image_id;
                if (id == null || String(id) === "") return null;
                return { id: String(id), ...copyPersistedImageFields(entry) };
            })
            .filter(Boolean);
    }
    if (Array.isArray(band.images) && band.images.length) {
        return band.images
            .map(entry => ({ id: entry?.image_id ?? entry?.id }))
            .filter(x => x.id != null && String(x.id) !== "")
            .map(x => ({ id: String(x.id) }));
    }
    return [];
}

function tryResolveThumbFromFlat(rec) {
    if (!rec || typeof rec !== "object") return "";
    const direct =
        rec.s3_thumbnail_url ||
        rec.thumbnail_url ||
        rec.medium_image_url ||
        rec.image_url ||
        rec.url ||
        "";
    if (direct) return direct;

    const isEdited = !!rec.is_edited;
    if (isEdited) {
        return rec.s3_ethumbnail_key || rec.s3_eimage_thumb_key || rec.s3_eimage_key || "";
    }
    return rec.s3_thumbnail_key || rec.s3_thumb_key || rec.s3_preview_key || rec.s3_image_key || "";
}

/** Full-size image for band slots and previews (not thumbnail-first). */
function tryResolveDisplayFromFlat(rec) {
    if (!rec || typeof rec !== "object") return "";
    const direct =
        rec.s3_image_url ||
        rec.image_url ||
        rec.medium_image_url ||
        rec.url ||
        rec.s3_image_key ||
        rec.thumbnail_url ||
        "";
    if (direct) return direct;

    const isEdited = !!rec.is_edited;
    if (isEdited) {
        return rec.s3_eimage_key || rec.s3_eimage_thumb_key || rec.s3_ethumbnail_key || "";
    }
    return rec.s3_image_key || rec.s3_preview_key || rec.s3_thumb_key || rec.s3_thumbnail_key || rec.s3_thumbnail_url || "";
}

function resolveExecutiveSummaryImageDisplaySrc(imageRecord) {
    if (!imageRecord || typeof imageRecord !== "object") return "";
    const fromSelf = tryResolveDisplayFromFlat(imageRecord);
    if (fromSelf) return fromSelf;
    const media = getExecutiveSummaryImageMediaRecord(imageRecord);
    if (media && media !== imageRecord) {
        return tryResolveDisplayFromFlat(media);
    }
    return "";
}

/** Building assignment rows often nest file metadata on master_image / image. */
export function getExecutiveSummaryImageMediaRecord(imageRecord) {
    if (!imageRecord || typeof imageRecord !== "object") return null;
    if (imageRecord.master_image && typeof imageRecord.master_image === "object") {
        return imageRecord.master_image;
    }
    if (imageRecord.image && typeof imageRecord.image === "object" && !Array.isArray(imageRecord.image)) {
        return imageRecord.image;
    }
    return imageRecord;
}

export function collectBuildingImageLookupIds(img) {
    if (!img || typeof img !== "object") return [];
    const keys = new Set();
    [img.id, img.image_id, img.building_image_id, img.master_image?.id].forEach(k => {
        if (k != null && String(k) !== "") keys.add(String(k));
    });
    return [...keys];
}

/** Alternate id keys stored on exec-summary slot entries — must match mapping keys used in `buildBuildingImageMap`. */
export function execSummaryImageEntryLookupKeys(entry) {
    if (!entry || typeof entry !== "object") return [];
    const keys = new Set();
    [entry.id, entry.image_id, entry.building_image_id].forEach(k => {
        if (k != null && String(k) !== "") keys.add(String(k));
    });
    return [...keys];
}

export function findBuildingImageRecord(imageList, idStr) {
    const sid = String(idStr ?? "");
    if (!sid) return null;
    return (
        (Array.isArray(imageList) ? imageList : []).find(img => collectBuildingImageLookupIds(img).includes(sid)) ||
        null
    );
}

export function buildExecSummaryImageDataFromBuildingPick(img) {
    return toExecSummaryImageDataEntry(getBuildingImageRecordId(img));
}

/** Client-side band data: keep id plus URL fields for preview (API save still slims to { id }). */
export function normalizeExecSummaryImageDataForEditor(entries) {
    return (Array.isArray(entries) ? entries : [])
        .map(entry => {
            if (!entry || typeof entry !== "object") return null;
            const id = entry.id ?? entry.image_id;
            if (id == null || String(id) === "") return null;
            return { id: String(id), ...copyPersistedImageFields(entry) };
        })
        .filter(Boolean);
}



export function buildExecSummaryImageDataFromMasterPick(img) {
    if (!img || typeof img !== "object") return null;
    const id = img.id != null ? String(img.id) : getBuildingImageRecordId(img);
    if (!id) return null;
    const fields = copyPersistedImageFields(img);
    if (!fields.url && img.url) fields.url = img.url;
    if (!fields.s3_image_key && !fields.s3_eimage_key && img.url) {
        if (img.is_edited) fields.s3_eimage_key = img.url;
        else fields.s3_image_key = img.url;
    }
    return { id, ...fields };
}

/** Append gallery master picks onto existing band slots without replacing building picks (dedupe by id). */
export function mergeExecutiveSummaryGallerySlots(existingEntries, galleryPickedImages) {
    const rawExisting = Array.isArray(existingEntries) ? existingEntries : [];
    const existing = normalizeExecSummaryImageDataForEditor(existingEntries);
    const seen = new Set();
    rawExisting.forEach(entry => {
        execSummaryImageEntryLookupKeys(entry).forEach(k => seen.add(k));
    });
    existing.forEach(entry => {
        execSummaryImageEntryLookupKeys(entry).forEach(k => seen.add(k));
    });

    const merged = [...existing];
    (Array.isArray(galleryPickedImages) ? galleryPickedImages : []).forEach(img => {
        const entry = buildExecSummaryImageDataFromMasterPick(img);
        if (!entry) return;
        const keys = [...collectBuildingImageLookupIds(img), ...execSummaryImageEntryLookupKeys(entry)];
        if (keys.some(k => seen.has(k))) return;
        keys.forEach(k => seen.add(k));
        merged.push(entry);
    });
    return clampExecutiveSummaryImageData(merged);
}

export function mergeExecutiveSummaryBuildingPickSlots(existingEntries, selectedBuildingImageIds, buildingImages) {
    const existing = normalizeExecSummaryImageDataForEditor(existingEntries);
    const seen = new Set();
    existing.forEach(entry => {
        execSummaryImageEntryLookupKeys(entry).forEach(k => seen.add(k));
    });

    const imageMap = buildBuildingImageMap(buildingImages);
    const merged = [...existing];
    (Array.isArray(selectedBuildingImageIds) ? selectedBuildingImageIds : []).forEach(id => {
        const sid = String(id ?? "");
        if (!sid) return;
        const img = imageMap.get(sid);
        const keys = img ? collectBuildingImageLookupIds(img) : [sid];
        if (keys.some(k => seen.has(k))) return;

        keys.forEach(k => seen.add(k));
        merged.push(img ? { id: sid, ...img, ...(img.master_image || {}) } : { id: sid });
    });
    return clampExecutiveSummaryImageData(normalizeExecSummaryImageDataForEditor(merged));
}

function indexExistingExecutiveBandsByKey(existingBands, map = new Map()) {
    (Array.isArray(existingBands) ? existingBands : []).forEach(band => {
        if (!band || typeof band !== "object") return;
        [band.key, band.id, band.label, getExecutiveSummaryBandLabel(band)].forEach(k => {
            if (k != null && String(k) !== "") map.set(String(k), band);
        });
        if (isExecutiveSummaryCombBand(band)) {
            indexExistingExecutiveBandsByKey(getCombBandChildren(band), map);
        }
    });
    return map;
}

function mergeOneExecutiveImageBandFromExisting(band, existingByKey) {
    if (!band || typeof band !== "object" || !isExecutiveSummaryImageBand(band)) return band;
    const existingBand =
        existingByKey.get(String(band.key ?? "")) ||
        existingByKey.get(String(band.id ?? "")) ||
        existingByKey.get(String(band.label ?? "")) ||
        existingByKey.get(String(getExecutiveSummaryBandLabel(band)));
    if (!existingBand || !isExecutiveSummaryImageBand(existingBand)) return band;

    const existingEntriesByKey = new Map();
    getExecSummaryImageData(existingBand).forEach(entry => {
        execSummaryImageEntryLookupKeys(entry).forEach(k => {
            if (k) existingEntriesByKey.set(k, entry);
        });
    });

    const data = getExecSummaryImageData(band).map(entry => {
        const previous = execSummaryImageEntryLookupKeys(entry)
            .map(k => existingEntriesByKey.get(k))
            .find(Boolean);
        return previous ? { ...previous, ...entry } : entry;
    });

    return {
        ...band,
        label: getExecutiveSummaryBandLabel(band) || band.label,
        type: "image_band",
        data: normalizeExecSummaryImageDataForEditor(data),
        images: undefined,
        band_kind: undefined,
        image_layout: undefined
    };
}

export function mergeExecutiveSummaryImageMetadataFromExisting(incomingBands, existingBands) {
    const existingByKey = indexExistingExecutiveBandsByKey(existingBands);

    const mapBand = band => {
        if (!band || typeof band !== "object") return band;
        if (isExecutiveSummaryCombBand(band)) {
            return {
                ...band,
                bands: getCombBandChildren(band).map(mapBand)
            };
        }
        return mergeOneExecutiveImageBandFromExisting(band, existingByKey);
    };

    return (Array.isArray(incomingBands) ? incomingBands : []).map(mapBand);
}

export function getBuildingImagesArray(regionData) {
    if (!regionData || typeof regionData !== "object") return [];
    const candidates = [
        regionData.data,
        regionData.results,
        regionData.images,
        regionData.image_list,
        Array.isArray(regionData) ? regionData : null
    ];
    for (const c of candidates) {
        if (Array.isArray(c)) return c;
    }
    return [];
}

export function getBuildingImageRecordId(img) {
    if (!img || typeof img !== "object") return "";
    const id = img.id ?? img.image_id ?? img.building_image_id;
    return id != null && String(id) !== "" ? String(id) : "";
}

export function buildBuildingImageMap(imageList) {
    const m = new Map();
    (Array.isArray(imageList) ? imageList : []).forEach(img => {
        collectBuildingImageLookupIds(img).forEach(k => m.set(k, img));
    });
    return m;
}

export function resolveExecutiveSummaryImageSrc(imageRecord) {
    if (!imageRecord || typeof imageRecord !== "object") return "";
    const fromSelf = tryResolveThumbFromFlat(imageRecord);
    if (fromSelf) return fromSelf;
    const media = getExecutiveSummaryImageMediaRecord(imageRecord);
    if (media && media !== imageRecord) {
        return tryResolveThumbFromFlat(media);
    }
    return "";
}

export function resolveExecSummaryImageDataEntrySrc(entry, imageMap) {
    if (!entry) return "";
    const fromEntry = resolveExecutiveSummaryImageSrc(entry);
    if (fromEntry) return fromEntry;

    if (imageMap) {
        const keys = execSummaryImageEntryLookupKeys(entry);
        for (let i = 0; i < keys.length; i += 1) {
            const k = keys[i];
            if (!k || !imageMap.has(k)) continue;
            const mapped = resolveExecutiveSummaryImageSrc(imageMap.get(k));
            if (mapped) return mapped;
        }
    }
    return "";
}

/** Band slot grid — prefer full image over thumbnail. */
export function resolveExecSummaryImageDataEntryDisplaySrc(entry, imageMap) {
    if (!entry) return "";
    const fromEntry = resolveExecutiveSummaryImageDisplaySrc(entry);
    if (fromEntry) return fromEntry;

    if (imageMap) {
        const keys = execSummaryImageEntryLookupKeys(entry);
        for (let i = 0; i < keys.length; i += 1) {
            const k = keys[i];
            if (!k || !imageMap.has(k)) continue;
            const fromMap = resolveExecutiveSummaryImageDisplaySrc(imageMap.get(k));
            if (fromMap) return fromMap;
        }
    }
    return resolveExecSummaryImageDataEntrySrc(entry, imageMap);
}

const CLIENT_ID_KEYS = ["_execClientId"];

/** @typedef {number[]} ExecutiveSummaryBandPath */

export function isExecutiveSummaryBandPath(path) {
    return (
        Array.isArray(path) &&
        path.length > 0 &&
        path.length <= 2 &&
        path.every(i => Number.isInteger(i) && i >= 0)
    );
}

export function bandPathKey(path) {
    return path.join("-");
}

export function bandPathToRowKey(path, band) {
    const clientId = band?._execClientId ?? band?.id ?? band?.key ?? "band";
    return `path-${bandPathKey(path)}-${sanitizeRbdId(String(clientId))}`;
}

export function flattenExecutiveSummaryBandsForLabelCheck(bands) {
    const out = [];
    const walk = list => {
        (Array.isArray(list) ? list : []).forEach(band => {
            if (!band || typeof band !== "object") return;
            out.push(band);
            if (isExecutiveSummaryCombBand(band)) walk(getCombBandChildren(band));
        });
    };
    walk(bands);
    return out;
}

function stripExecutiveSummaryHtmlLength(html) {
    return stripExecutiveSummaryHtml(html).length;
}

function pickRicherTradeBandValue(a, b) {
    const aVal = a?.value ?? "";
    const bVal = b?.value ?? "";
    if (!String(aVal).trim()) return bVal;
    if (!String(bVal).trim()) return aVal;
    return stripExecutiveSummaryHtmlLength(aVal) >= stripExecutiveSummaryHtmlLength(bVal) ? aVal : bVal;
}

function mergeTradeExecutiveSummaryBands(primary, secondary, tradeById) {
    const merged = { ...primary, ...secondary };
    merged.value = pickRicherTradeBandValue(primary, secondary);
    const tradeId = primary?.id != null ? String(primary.id) : "";
    const trade = tradeId && tradeById ? tradeById.get(tradeId) : null;
    if (trade) {
        const description = String(trade.description ?? "").trim();
        if (description) {
            merged.key = description;
        }
    }
    return merged;
}

export function dedupeExecutiveSummaryTradeBands(bands, trades) {
    const tradeById = new Map();
    (Array.isArray(trades) ? trades : []).forEach(t => {
        if (t?.id != null && String(t.id) !== "") tradeById.set(String(t.id), t);
    });

    const dedupeList = list => {
        const seen = new Map();
        const out = [];
        (Array.isArray(list) ? list : []).forEach(band => {
            if (!band || typeof band !== "object") {
                out.push(band);
                return;
            }
            if (isExecutiveSummaryCombBand(band)) {
                out.push({
                    ...band,
                    bands: dedupeList(getCombBandChildren(band))
                });
                return;
            }
            if (band.type !== "trade" || band.id == null || String(band.id) === "") {
                out.push(band);
                return;
            }
            const tid = String(band.id);
            if (seen.has(tid)) {
                const idx = seen.get(tid);
                out[idx] = mergeTradeExecutiveSummaryBands(out[idx], band, tradeById);
                return;
            }
            seen.set(tid, out.length);
            const next = { ...band };
            const trade = tradeById.get(tid);
            if (trade) {
                const description = String(trade.description ?? "").trim();
                if (description) {
                    next.key = description;
                }
            }
            out.push(next);
        });
        return out;
    };

    return dedupeList(bands);
}

/** Plain text from CKEditor HTML for executive summary search. */
export function stripExecutiveSummaryHtml(html) {
    if (html == null || html === "") return "";
    const str = String(html);
    if (typeof document !== "undefined") {
        const div = document.createElement("div");
        div.innerHTML = str;
        return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    }
    return str
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim();
}

/** Label, body text, and image captions for search matching. */
export function collectExecutiveSummarySearchableText(band, options = {}) {
    if (!band || typeof band !== "object") return "";
    const { imageMap } = options;
    const parts = [];
    const label = getExecutiveSummaryBandLabel(band);
    if (label) parts.push(label);

    const bodyText = stripExecutiveSummaryHtml(band.value ?? "");
    if (bodyText) parts.push(bodyText);

    if (isExecutiveSummarySampleTextBand(band)) {
        const sampleText = stripExecutiveSummaryHtml(getExecutiveSummaryBandSampleText(band));
        if (sampleText) parts.push(sampleText);
    }

    if (isExecutiveSummaryImageBand(band)) {
        getExecSummaryImageData(band).forEach(entry => {
            if (entry?.caption) parts.push(String(entry.caption));
            const id = entry?.id;
            if (imageMap && id != null) {
                const rec = imageMap.get(String(id));
                if (rec?.caption) parts.push(String(rec.caption));
            }
        });
    }

    return parts.join(" ");
}

/**
 * Flat search index for executive summary bands (top-level and nested).
 * @returns {{ rowKey: string, bandPath: number[], label: string, haystack: string }[]}
 */
export function buildExecutiveSummarySearchIndex(bands, options = {}) {
    const { imageMap } = options;
    const entries = [];
    const walk = (list, parentPath = []) => {
        (Array.isArray(list) ? list : []).forEach((band, index) => {
            if (!band || typeof band !== "object") return;
            const bandPath = [...parentPath, index];
            entries.push({
                rowKey: bandPathToRowKey(bandPath, band),
                bandPath,
                label: getExecutiveSummaryBandLabel(band),
                haystack: collectExecutiveSummarySearchableText(band, { imageMap })
            });
            if (isExecutiveSummaryCombBand(band)) {
                walk(getCombBandChildren(band), bandPath);
            }
        });
    };
    walk(bands);
    return entries;
}

/** Case-insensitive occurrence positions for `query` within `text`. */
function escapeExecSumSearchRegExp(text) {
    return String(text ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Build a search regex aligned with CKEditor FindAndReplace whole-word behavior. */
export function buildExecSumSearchRegex(query, options = {}) {
    const q = String(query ?? "").trim();
    if (!q) return null;
    const { matchCase = false, wholeWord = false } = options;
    let flags = "gu";
    if (!matchCase) flags += "i";

    let regExpQuery = `(${escapeExecSumSearchRegExp(q)})`;
    if (wholeWord) {
        const nonLetterGroup = "[^a-zA-Z\\u00C0-\\u024F\\u1E00-\\u1EFF]";
        if (!new RegExp(`^${nonLetterGroup}`).test(q)) {
            regExpQuery = `(^|${nonLetterGroup}|_)${regExpQuery}`;
        }
        if (!new RegExp(`${nonLetterGroup}$`).test(q)) {
            regExpQuery = `${regExpQuery}(?=_|${nonLetterGroup}|$)`;
        }
    }

    return new RegExp(regExpQuery, flags);
}

export function findSearchOccurrences(text, query, options = {}) {
    const value = String(text ?? "");
    const regex = buildExecSumSearchRegex(query, options);
    if (!value || !regex) return [];

    const results = [];
    for (const match of value.matchAll(regex)) {
        const captured = match[1] ?? match[0];
        const offset = match[0].indexOf(captured);
        const start = (match.index ?? 0) + (offset >= 0 ? offset : 0);
        results.push({ start, end: start + captured.length });
    }
    return results;
}

export function textMatchesExecSumSearch(text, query, options = {}) {
    const regex = buildExecSumSearchRegex(query, options);
    if (!regex) return false;
    return regex.test(String(text ?? ""));
}

/**
 * Flat occurrence-level search matches across label, body, and image captions.
 * @returns {{ rowKey: string, bandPath: number[], field: 'label'|'body'|'caption', occurrenceIndex: number, captionSlotIndex: number|null, matchStart: number, matchEnd: number }[]}
 */
export function buildExecutiveSummarySearchMatches(bands, query, options = {}) {
    const q = String(query ?? "").trim();
    if (!q || !Array.isArray(bands)) return [];
    const { imageMap, matchCase = false, wholeWord = false } = options;
    const searchOptions = { matchCase, wholeWord };
    const matches = [];

    const pushFieldMatches = (rowKey, bandPath, field, text, captionSlotIndex = null) => {
        findSearchOccurrences(text, q, searchOptions).forEach((occ, occurrenceIndex) => {
            matches.push({
                rowKey,
                bandPath,
                field,
                occurrenceIndex,
                captionSlotIndex,
                matchStart: occ.start,
                matchEnd: occ.end
            });
        });
    };

    const walk = (list, parentPath = []) => {
        (Array.isArray(list) ? list : []).forEach((band, index) => {
            if (!band || typeof band !== "object") return;
            const bandPath = [...parentPath, index];
            const rowKey = bandPathToRowKey(bandPath, band);
            const label = getExecutiveSummaryBandLabel(band);
            pushFieldMatches(rowKey, bandPath, "label", label);
            pushFieldMatches(rowKey, bandPath, "body", stripExecutiveSummaryHtml(band.value ?? ""));

            if (isExecutiveSummaryImageBand(band)) {
                getExecSummaryImageData(band).forEach((entry, slotIndex) => {
                    if (entry?.caption) {
                        pushFieldMatches(rowKey, bandPath, "caption", String(entry.caption), slotIndex);
                    }
                    const id = entry?.id;
                    if (imageMap && id != null) {
                        const rec = imageMap.get(String(id));
                        if (rec?.caption) {
                            pushFieldMatches(rowKey, bandPath, "caption", String(rec.caption), slotIndex);
                        }
                    }
                });
            }

            if (isExecutiveSummaryCombBand(band)) {
                walk(getCombBandChildren(band), bandPath);
            }
        });
    };

    walk(bands);
    return matches;
}

/** Group band meta: text/image breakdown for all children (e.g. "1 text band · 2 image bands"). */
export function formatCombBandMeta(children) {
    const list = (Array.isArray(children) ? children : []).filter(Boolean);
    if (!list.length) return "Empty group";

    const textCount = list.filter(child => !isExecutiveSummaryImageBand(child)).length;
    const imageCount = list.filter(child => isExecutiveSummaryImageBand(child)).length;
    const parts = [];
    if (textCount) parts.push(`${textCount} text band${textCount === 1 ? "" : "s"}`);
    if (imageCount) parts.push(`${imageCount} image band${imageCount === 1 ? "" : "s"}`);
    return parts.length ? parts.join(" · ") : "Empty group";
}

/** Heading and body lines for the remove-band confirmation modal. */
export function getExecutiveSummaryRemoveBandConfirmMessage(bands, bandPath, options = {}) {
    const { multiEdit = false } = options;
    const band = getBandAtPath(bands, bandPath);
    const label = getExecutiveSummaryBandLabel(band);
    const quotedLabel = label ? `"${label}"` : "This band";
    const persistLine = "You'll need to Save changes to persist.";
    const multiEditLine = multiEdit
        ? "Only buildings that already have this band will be affected."
        : null;

    const withPersist = lines => {
        const result = [...lines, persistLine];
        if (multiEditLine) result.push(multiEditLine);
        return result;
    };

    if (!band) {
        return {
            heading: "Remove band?",
            lines: withPersist(["This removes the band from the executive summary."])
        };
    }

    if (isExecutiveSummaryCombBand(band)) {
        const children = getCombBandChildren(band);
        if (children.length) {
            return {
                heading: "Remove group?",
                lines: withPersist([
                    `${quotedLabel} is a group containing ${formatCombBandMeta(children)}.`,
                    "Removing it will delete the group and all nested bands."
                ])
            };
        }
        return {
            heading: "Remove empty group?",
            lines: withPersist([`${quotedLabel} is an empty group.`, "Remove it from the executive summary?"])
        };
    }

    if (isExecutiveSummaryImageBand(band)) {
        const imageCount = getExecSummaryImageData(band).length;
        if (imageCount > 0) {
            return {
                heading: "Remove image band?",
                lines: withPersist([
                    `${quotedLabel} contains ${imageCount} image${imageCount === 1 ? "" : "s"}.`,
                    "Removing it will delete the band and its image assignments."
                ])
            };
        }
        return {
            heading: "Remove empty image band?",
            lines: withPersist([`${quotedLabel} has no images.`, "Remove this image band from the executive summary?"])
        };
    }

    const bodyText = stripExecutiveSummaryHtml(band.value ?? "");
    if (bodyText) {
        return {
            heading: "Remove text band?",
            lines: withPersist([
                `${quotedLabel} contains text content.`,
                "Removing it will delete the band and its content."
            ])
        };
    }

    return {
        heading: "Remove empty text band?",
        lines: withPersist([`${quotedLabel} has no content.`, "Remove this band from the executive summary?"])
    };
}

/** Caption text for an image band slot (entry caption or imageMap fallback). */
export function getExecutiveSummaryImageSlotCaption(band, slotIndex, options = {}) {
    if (!band || !isExecutiveSummaryImageBand(band)) return "";
    const { imageMap } = options;
    const entry = getExecSummaryImageData(band)[slotIndex];
    if (!entry) return "";
    if (entry.caption) return String(entry.caption);
    const id = entry.id;
    if (imageMap && id != null) {
        const rec = imageMap.get(String(id));
        if (rec?.caption) return String(rec.caption);
    }
    return "";
}

/**
 * @param {ReturnType<typeof buildExecutiveSummarySearchIndex>} index
 * @returns {({ rowKey: string, bandPath: number[], label: string, haystack: string })[]}
 */
export function filterExecutiveSummarySearchIndex(index, query) {
    const q = String(query ?? "")
        .trim()
        .toLowerCase();
    if (!q) return [];
    return (Array.isArray(index) ? index : [])
        .map(entry => {
            const labelLower = String(entry.label ?? "").toLowerCase();
            const hayLower = String(entry.haystack ?? "").toLowerCase();
            if (!labelLower.includes(q) && !hayLower.includes(q)) return null;
            return entry;
        })
        .filter(Boolean);
}

/** Row keys for parent comb groups that must expand to reveal a nested band path. */
export function getExecutiveSummaryAncestorGroupRowKeys(bands, bandPath) {
    if (!Array.isArray(bands) || !isExecutiveSummaryBandPath(bandPath) || bandPath.length < 2) {
        return [];
    }
    const parent = bands[bandPath[0]];
    if (!parent || !isExecutiveSummaryCombBand(parent)) return [];
    return [bandPathToRowKey([bandPath[0]], parent)];
}

/** Collect all band row keys (top-level and comb children) for expand/collapse-all. */
export function collectExecutiveSummaryRowKeys(bands) {
    const rowKeys = [];
    (Array.isArray(bands) ? bands : []).forEach((band, index) => {
        if (!band || typeof band !== "object") return;
        const bandPath = [index];
        rowKeys.push(bandPathToRowKey(bandPath, band));
        if (isExecutiveSummaryCombBand(band)) {
            getCombBandChildren(band).forEach((child, childIndex) => {
                if (!child || typeof child !== "object") return;
                rowKeys.push(bandPathToRowKey([index, childIndex], child));
            });
        }
    });
    return rowKeys;
}

/** Collect row keys to expand for search: all matches plus ancestor groups. */
export function collectExecutiveSummarySearchExpandKeys(bands, matches) {
    const keys = new Set();
    (Array.isArray(matches) ? matches : []).forEach(match => {
        keys.add(match.rowKey);
        getExecutiveSummaryAncestorGroupRowKeys(bands, match.bandPath).forEach(k => keys.add(k));
    });
    return [...keys];
}

/** Recursively map band keys/ids/labels to stable `_execClientId` for editor normalization. */
export function buildExecutiveSummaryIdByKey(bands, map = new Map()) {
    (Array.isArray(bands) ? bands : []).forEach(band => {
        if (!band || typeof band !== "object") return;
        const clientId = band._execClientId;
        if (clientId != null && String(clientId) !== "") {
            if (band.key != null && String(band.key) !== "") map.set(String(band.key), clientId);
            if (band.id != null && String(band.id) !== "") map.set(String(band.id), clientId);
            const label = getExecutiveSummaryBandLabel(band);
            if (label) map.set(label, clientId);
        }
        if (isExecutiveSummaryCombBand(band)) {
            buildExecutiveSummaryIdByKey(getCombBandChildren(band), map);
        }
    });
    return map;
}

export function getBandAtPath(bands, path) {
    if (!Array.isArray(bands) || !isExecutiveSummaryBandPath(path)) return null;
    if (path.length === 1) return bands[path[0]] ?? null;
    const parent = bands[path[0]];
    if (!isExecutiveSummaryCombBand(parent)) return null;
    const children = getCombBandChildren(parent);
    return children[path[1]] ?? null;
}

function cloneExecutiveSummaryList(list) {
    return (Array.isArray(list) ? list : []).map(b => {
        if (!b || typeof b !== "object") return b;
        if (isExecutiveSummaryCombBand(b)) {
            return { ...b, bands: cloneExecutiveSummaryList(getCombBandChildren(b)) };
        }
        return { ...b };
    });
}

/** Deep clone bands for organizer modal draft state. */
export function cloneExecutiveSummaryBandsForEditor(bands) {
    return cloneExecutiveSummaryList(bands);
}

/**
 * Reorder a band within its current container (top level or within one group).
 * @param {object[]} bands
 * @param {number[]} fromPath
 * @param {number} toIndex — target index in the same container
 */
export function reorderBandAtPath(bands, fromPath, toIndex) {
    if (!isExecutiveSummaryBandPath(fromPath)) return bands;
    const list = cloneExecutiveSummaryList(bands);

    if (fromPath.length === 1) {
        const fromIdx = fromPath[0];
        let toIdx = toIndex;
        if (!Number.isInteger(toIdx) || toIdx < 0 || fromIdx >= list.length) return bands;
        if (fromIdx === toIdx) return bands;
        const top = [...list];
        const [item] = top.splice(fromIdx, 1);
        toIdx = Math.max(0, Math.min(toIdx, top.length));
        top.splice(toIdx, 0, item);
        return top;
    }

    if (fromPath.length === 2) {
        const parentIdx = fromPath[0];
        const fromIdx = fromPath[1];
        const parent = list[parentIdx];
        if (!isExecutiveSummaryCombBand(parent)) return bands;
        let toIdx = toIndex;
        const children = [...getCombBandChildren(parent)];
        if (!Number.isInteger(toIdx) || toIdx < 0 || fromIdx >= children.length) return bands;
        if (fromIdx === toIdx) return bands;
        const [item] = children.splice(fromIdx, 1);
        toIdx = Math.max(0, Math.min(toIdx, children.length));
        children.splice(toIdx, 0, item);
        return setCombBandChildren(list, parentIdx, children);
    }

    return bands;
}

/**
 * Move a band between top level and a group container.
 * @param {object[]} bands
 * @param {number[]} fromPath
 * @param {number[]|null} toParentPath — null/[] for top level, [groupIdx] for inside a group
 * @param {number} insertIndex — index within the target container
 */
export function moveBandBetweenContainers(bands, fromPath, toParentPath, insertIndex) {
    if (!isExecutiveSummaryBandPath(fromPath)) return bands;
    const list = cloneExecutiveSummaryList(bands);
    const moving = getBandAtPath(list, fromPath);
    if (!moving) return bands;

    if (isExecutiveSummaryCombBand(moving)) {
        if (fromPath.length > 1) return bands;
        if (toParentPath != null && toParentPath.length > 0) return bands;
    }

    const without = removeBandAtPath(list, fromPath);
    const toTopLevel = toParentPath == null || toParentPath.length === 0;

    if (toTopLevel) {
        if (fromPath.length === 1 && isExecutiveSummaryCombBand(moving)) {
            let idx = insertIndex;
            if (!Number.isInteger(idx) || idx < 0) idx = without.length;
            if (fromPath[0] < idx) idx -= 1;
            idx = Math.max(0, Math.min(idx, without.length));
            return insertBandAtPath(without, moving, [idx]);
        }
        if (fromPath.length !== 2) return bands;
        let idx = insertIndex;
        if (!Number.isInteger(idx) || idx < 0) idx = without.length;
        idx = Math.max(0, Math.min(idx, without.length));
        return insertBandAtPath(without, moving, [idx]);
    }

    if (toParentPath.length === 1) {
        if (isExecutiveSummaryCombBand(moving)) return bands;
        let parentIdx = toParentPath[0];
        if (fromPath.length === 1 && fromPath[0] < parentIdx) parentIdx -= 1;
        const parent = without[parentIdx];
        if (!isExecutiveSummaryCombBand(parent)) return bands;
        let idx = insertIndex;
        if (!Number.isInteger(idx) || idx < 0) idx = getCombBandChildren(parent).length;
        if (fromPath.length === 2 && fromPath[0] === parentIdx && fromPath[1] < idx) idx -= 1;
        idx = Math.max(0, Math.min(idx, getCombBandChildren(parent).length));
        return insertBandAtPath(without, moving, [parentIdx, idx]);
    }

    return bands;
}

function setCombBandChildren(bands, parentIndex, children) {
    return bands.map((b, i) => (i === parentIndex ? { ...b, bands: children } : b));
}

export function updateBandAtPath(bands, path, partial) {
    const list = cloneExecutiveSummaryList(bands);
    if (!isExecutiveSummaryBandPath(path)) return list;
    if (path.length === 1) {
        const cur = list[path[0]];
        if (!cur) return list;
        list[path[0]] = { ...cur, ...partial };
        return list;
    }
    const parentIdx = path[0];
    const parent = list[parentIdx];
    if (!isExecutiveSummaryCombBand(parent)) return list;
    const children = cloneExecutiveSummaryList(getCombBandChildren(parent));
    const cur = children[path[1]];
    if (!cur) return list;
    children[path[1]] = { ...cur, ...partial };
    return setCombBandChildren(list, parentIdx, children);
}

export function removeBandAtPath(bands, path) {
    const list = cloneExecutiveSummaryList(bands);
    if (!isExecutiveSummaryBandPath(path)) return list;
    if (path.length === 1) return list.filter((_, i) => i !== path[0]);
    const parentIdx = path[0];
    const parent = list[parentIdx];
    if (!isExecutiveSummaryCombBand(parent)) return list;
    const children = getCombBandChildren(parent).filter((_, i) => i !== path[1]);
    return setCombBandChildren(list, parentIdx, children);
}

export function insertBandAtPath(bands, band, path) {
    const list = cloneExecutiveSummaryList(bands);
    if (!band) return list;
    if (!path || path.length === 0) return [...list, band];
    if (path.length === 1) {
        const idx = path[0];
        const next = [...list];
        next.splice(idx, 0, band);
        return next;
    }
    if (path.length === 2) {
        const parentIdx = path[0];
        const parent = list[parentIdx];
        if (!isExecutiveSummaryCombBand(parent)) return list;
        const children = [...getCombBandChildren(parent)];
        children.splice(path[1], 0, band);
        return setCombBandChildren(list, parentIdx, children);
    }
    return list;
}

export function ensureExecutiveSummaryBandIds(bands) {
    const mapBand = band => {
        if (!band || typeof band !== "object") return band;
        const id = band.id != null && String(band.id) !== "" ? String(band.id) : generateExecSummaryBandId();
        if (isExecutiveSummaryCombBand(band)) {
            return {
                ...band,
                id,
                type: "comb_band",
                bands: getCombBandChildren(band).map(mapBand)
            };
        }
        if (isExecutiveSummaryImageBand(band)) {
            return {
                ...band,
                id,
                label: getExecutiveSummaryBandLabel(band) || band.label,
                type: "image_band"
            };
        }
        return { ...band, id };
    };
    return (Array.isArray(bands) ? bands : []).map(mapBand);
}

export function assignExecutiveBandClientId(band, buildingId, index, previousId, pathPrefix = "") {
    const pathPart = pathPrefix ? `${pathPrefix}-` : "";
    const previousClientId =
        previousId && typeof previousId === "object" ? previousId._execClientId : previousId;
    const base =
        previousClientId != null && String(previousClientId) !== ""
            ? String(previousClientId)
            : `exec-${buildingId ?? "new"}-${pathPart}${index}-${band?.key ?? "k"}-${Date.now()}`;
    return { ...band, _execClientId: base };
}

export function stripExecutiveBandClientIds(bands) {
    if (!Array.isArray(bands)) return [];
    return bands.map(b => {
        if (!b || typeof b !== "object") return b;
        const next = { ...b };
        CLIENT_ID_KEYS.forEach(k => delete next[k]);
        if (isExecutiveSummaryCombBand(next)) {
            next.bands = stripExecutiveBandClientIds(getCombBandChildren(next));
        }
        return next;
    });
}

function normalizeOneBandForCompare(b) {
    if (!b || typeof b !== "object") return {};
    if (isExecutiveSummaryCombBand(b)) {
        return {
            key: b.key,
            label: getExecutiveSummaryBandLabel(b),
            type: "comb_band",
            ...(b.id != null && b.id !== "" ? { id: String(b.id) } : {}),
            value: b.value ?? "",
            bands: getCombBandChildren(b).map(normalizeOneBandForCompare)
        };
    }
    if (isExecutiveSummaryImageBand(b)) {
        return appendQaStatusToLeafBandPayload(
            {
                key: b.key,
                label: getExecutiveSummaryBandLabel(b),
                type: "image_band",
                ...(b.id != null && b.id !== "" ? { id: String(b.id) } : {}),
                config: { type: getExecSummaryImageLayout(b) },
                data: getExecSummaryImageData(b).map(e => ({ id: String(e.id) }))
            },
            b
        );
    }
    return appendQaStatusToLeafBandPayload(
        appendSampleTextToLeafBandPayload(
            {
                key: b.key,
                label: getExecutiveSummaryBandLabel(b),
                type: b.type ?? "additional",
                ...(b.id != null && b.id !== "" ? { id: String(b.id) } : {}),
                value: b.value ?? ""
            },
            b
        ),
        b
    );
}

function normalizeOneBandForSave(b) {
    if (!b || typeof b !== "object") return {};
    const id = b.id != null && String(b.id) !== "" ? String(b.id) : generateExecSummaryBandId();
    if (isExecutiveSummaryCombBand(b)) {
        return {
            key: b.key,
            label: getExecutiveSummaryBandLabel(b),
            type: "comb_band",
            id,
            value: b.value ?? "",
            bands: getCombBandChildren(b).map(normalizeOneBandForSave)
        };
    }
    if (isExecutiveSummaryImageBand(b)) {
        const label = getExecutiveSummaryBandLabel(b);
        return appendQaStatusToLeafBandPayload(
            {
                key: b.key,
                label,
                name: label,
                type: "image_band",
                id,
                config: { type: getExecSummaryImageLayout(b) },
                data: slimExecSummaryImageDataEntries(getExecSummaryImageData(b))
            },
            b
        );
    }
    return appendQaStatusToLeafBandPayload(
        appendSampleTextToLeafBandPayload(
            {
                key: b.key,
                label: getExecutiveSummaryBandLabel(b),
                type: b.type ?? "additional",
                id,
                value: b.value ?? ""
            },
            b
        ),
        b
    );
}

export function normalizeExecutiveBandForEditor(band, buildingId, index, idByKey, pathPrefix = "") {
    const lookupKeys = [band?.key, band?.id, getExecutiveSummaryBandLabel(band)].filter(
        k => k != null && String(k) !== ""
    );
    let prev;
    if (idByKey) {
        lookupKeys.some(k => {
            if (idByKey.has(String(k))) {
                prev = idByKey.get(String(k));
                return true;
            }
            return false;
        });
    }

    if (isExecutiveSummaryCombBand(band)) {
        const children = getCombBandChildren(band).map((child, ci) =>
            normalizeExecutiveBandForEditor(child, buildingId, ci, idByKey, `${pathPrefix}${index}-`)
        );
        const base = assignExecutiveBandClientId(
            { ...band, type: "comb_band", bands: children },
            buildingId,
            index,
            prev,
            pathPrefix
        );
        lookupKeys.forEach(k => idByKey?.set(String(k), base._execClientId));
        return base;
    }

    if (isExecutiveSummaryImageBand(band)) {
        const base = assignExecutiveBandClientId(
            {
                ...band,
                label: getExecutiveSummaryBandLabel(band) || band.label,
                type: "image_band",
                qa_status: getExecutiveSummaryBandQaStatus(band),
                ...(Array.isArray(band.data) && band.data.length ? { data: band.data.map(d => ({ ...d })) } : {}),
                ...(Array.isArray(band.images) && band.images.length
                    ? { images: band.images.map(img => ({ ...img })) }
                    : {})
            },
            buildingId,
            index,
            prev,
            pathPrefix
        );
        lookupKeys.forEach(k => idByKey?.set(String(k), base._execClientId));
        return base;
    }

    const base = assignExecutiveBandClientId(
        { ...band, qa_status: getExecutiveSummaryBandQaStatus(band) },
        buildingId,
        index,
        prev,
        pathPrefix
    );
    lookupKeys.forEach(k => idByKey?.set(String(k), base._execClientId));
    return base;
}

/** Strip client-only fields and reduce to persisted shape for deep equality (dirty detection). */
export function normalizeExecutiveSummaryForCompare(bands) {
    if (!Array.isArray(bands)) return [];
    return stripExecutiveBandClientIds(bands).map(normalizeOneBandForCompare);
}

export function executiveBandsEqualForSave(a, b) {
    return isEqual(normalizeExecutiveSummaryForCompare(a), normalizeExecutiveSummaryForCompare(b));
}

/** Shape sent to API on save/update — strips client ids and image data metadata. */
export function normalizeExecutiveSummaryForSave(bands) {
    if (!Array.isArray(bands)) return [];
    return ensureExecutiveSummaryBandIds(stripExecutiveBandClientIds(bands).map(normalizeOneBandForSave));
}

export function buildExecutiveSummarySavePayload(bands) {
    const list = Array.isArray(bands) ? bands : [];
    return {
        executive_summary: normalizeExecutiveSummaryForSave(list),
        executive_summary_qa_status: getExecutiveSummaryBuildingQaStatus(list)
    };
}

/** RBD id: alphanumeric + hyphen only */
export function sanitizeRbdId(raw) {
    const s = String(raw ?? "x").replace(/[^a-zA-Z0-9_-]/g, "-");
    return s.length ? s.slice(0, 200) : "band";
}

function normalizeOneExecutiveSummaryBandBuilding(item, index) {
    if (!item || typeof item !== "object") return null;
    const name = String(item.name ?? "").trim();
    const description = String(item.description ?? "").trim();
    if (!name && !description) return null;
    return {
        id: item.id != null && String(item.id) !== "" ? String(item.id) : `building-${index}`,
        name,
        description
    };
}

/** Display label for multi-edit band building list — never uses id. */
export function formatExecutiveSummaryBandBuildingLabel(building) {
    if (!building || typeof building !== "object") return "";
    const name = String(building.name ?? "").trim();
    const description = String(building.description ?? "").trim();
    if (name && description) return `${name} (${description})`;
    return name || description || "";
}

export function getExecutiveSummaryBandBuildings(band) {
    if (!band || !Array.isArray(band.buildings)) return [];
    return band.buildings
        .map((item, index) => normalizeOneExecutiveSummaryBandBuilding(item, index))
        .filter(Boolean);
}

export function formatExecutiveSummaryBandForMultiEditSave(band) {
    if (!band || typeof band !== "object") {
        return { key: "", label: "", type: "", value: "" };
    }
    const formatted = {
        key: band.key ?? band.label ?? "",
        label: band.label ?? band.key ?? "",
        type: band.type ?? "",
        value: band.value ?? ""
    };
    if (band.id != null && String(band.id) !== "") {
        formatted.id = band.id;
    }
    if (isExecutiveSummaryLeafBand(band)) {
        formatted.qa_status = getExecutiveSummaryBandQaStatus(band);
    }
    return formatted;
}

function normalizeOneExecutiveSummaryBandForMultiEdit(item, fallbackKey) {
    if (item && typeof item === "object" && !Array.isArray(item)) {
        const label = item.label || item.description || item.key || fallbackKey;
        return {
            ...item,
            id: item.id ?? item.key ?? fallbackKey,
            label,
            key: item.key || label,
            value: item.value ?? "",
            buildings: Array.isArray(item.buildings) ? item.buildings : []
        };
    }
    return {
        id: fallbackKey,
        label: String(fallbackKey ?? ""),
        key: String(fallbackKey ?? ""),
        value: item ?? "",
        buildings: []
    };
}

/** Normalize `edit_multiple` executive_summary — backend returns a flattened text-band list. */
export function normalizeExecutiveSummaryForMultiEdit(raw) {
    const summary = raw?.value ?? raw;
    if (!summary) return [];
    if (Array.isArray(summary)) {
        return summary.map((item, index) =>
            normalizeOneExecutiveSummaryBandForMultiEdit(item, item?.id ?? item?.key ?? index)
        );
    }
    if (typeof summary === "object") {
        return Object.entries(summary).map(([key, value]) =>
            normalizeOneExecutiveSummaryBandForMultiEdit(value, key)
        );
    }
    return [];
}

export function findExecutiveSummaryBandByKey(list, keyOrBand) {
    if (!Array.isArray(list)) return null;
    const key =
        typeof keyOrBand === "object" && keyOrBand !== null
            ? keyOrBand.key ?? keyOrBand.label
            : keyOrBand;
    if (key == null || key === "") return null;
    return (
        list.find(band => band?.key === key) ??
        list.find(band => band?.label === key) ??
        null
    );
}
